/* eslint-disable camelcase */

import { IMAGES_ACTIVITY_ROOT } from '@ts/utils/constants';
import {
    ActivityArticle,
    ActivityArticlePreview,
    ActivityCartItem,
    ActivityPreview,
    ImpressionPayload,
    ImpressionResolver,
    ProductDetail,
    ProductDetailPayload,
    ProductInListPayload,
    ProductInListResolver,
    ProductPayload,
    ProductResolver,
    ProductCartPayload,
    ProductCartItem,
    Order,
    Purchase,
} from './types';

function categoryReplace(name: string) {
    return name.replace(' - ', '|');
}

export const createCategoryNameFromPreview = (activity: ActivityPreview) => {
    if (activity.analytics_data && activity.analytics_data.category) {
        return activity.analytics_data.category;
    }

    return '';
};

export const createCategoryNameFromArticle = (activity: ActivityArticle) => {
    if (
        activity.city_name &&
        activity.activity_categories &&
        activity.activity_categories.length > 0
    ) {
        return `${activity.city_name}|${activity.activity_categories[0].name}`;
    }

    return '';
};

export const createCategoryNameFromArticlePreview = (activity: ActivityArticlePreview) =>
    createCategoryNameFromArticle(activity as ActivityArticle); // temp hotfix

export const createCategoryNameFromCartItem = (activity: ActivityCartItem) => {
    if (activity.product_city_info && activity.product_category_info) {
        return `${activity.product_city_info.name}|${activity.product_activity ||
            activity.product_category_info.name}`;
    }

    return '';
};

// Impressions

export const createImpressionFromPreview = ({
    listName,
    categoryName,
    position,
}: ImpressionPayload): ImpressionResolver<ActivityPreview> => (activity) => ({
    list: listName,
    category: categoryReplace(categoryName),
    position,
    name: activity.name,
    id: activity.id,
    price: activity.price,
    brand: activity.analytics_data.brand,
});

export const createImpressionFromArticlePreview = ({
    listName,
    categoryName,
    position,
}: ImpressionPayload): ImpressionResolver<ActivityArticlePreview> => (activity) => ({
    list: listName,
    category: categoryReplace(categoryName),
    position,
    id: activity.id,
    name: activity.name,
    price: activity.price,
    brand: activity.title,
});

export const createImpressionFromArticle = ({
    listName,
    categoryName,
    position,
}: ImpressionPayload): ImpressionResolver<ActivityArticle> => (activity) => ({
    list: listName,
    category: categoryReplace(categoryName),
    position,
    id: activity.id,
    name: activity.name,
    price: activity.price,
    brand: activity.title,
});

export const createImpressionFromCartItem = ({
    listName,
    categoryName,
    position,
}: ImpressionPayload): ImpressionResolver<ActivityCartItem> => (activity) => ({
    list: listName,
    category: categoryReplace(categoryName),
    position,
    name: activity.product_name,
    id: activity.product_id,
    price: activity.partner_price,
    brand: activity.partner_title,
});

// Products

export const createProductFromPreview = ({
    categoryName,
}: ProductPayload): ProductResolver<ActivityPreview> => (activity) => ({
    category: categoryReplace(categoryName),
    name: activity.name,
    id: activity.id,
    price: activity.price,
    brand: activity.analytics_data.brand,
});

export const createProductFromArticlePreview = ({
    categoryName,
}: ProductPayload): ProductResolver<ActivityArticlePreview> => (activity) => ({
    category: categoryReplace(categoryName),
    id: activity.id,
    name: activity.name,
    price: activity.price,
    brand: activity.title,
});

export const createProductFromArticle = ({
    categoryName,
}: ProductPayload): ProductResolver<ActivityArticle> => (activity) => ({
    category: categoryReplace(categoryName),
    id: activity.id,
    name: activity.name,
    price: activity.price,
    brand: activity.title,
});

export const createProductFromCartItem = ({
    categoryName,
}: ProductPayload): ProductResolver<ActivityCartItem> => (activity) => ({
    category: categoryReplace(categoryName),
    name: activity.product_name,
    id: activity.product_id,
    price: activity.partner_price,
    brand: activity.partner_title,
});

// Products In List

export const createProductInListFromPreview = ({
    categoryName,
    position,
}: ProductInListPayload): ProductInListResolver<ActivityPreview> => (activity) => ({
    position,
    category: categoryReplace(categoryName),
    name: activity.name,
    id: activity.id,
    price: activity.price,
    brand: activity.analytics_data.brand,
});

export const createProductInListFromArticlePreview = ({
    categoryName,
    position,
}: ProductInListPayload): ProductInListResolver<ActivityArticlePreview> => (activity) => ({
    position,
    category: categoryReplace(categoryName),
    id: activity.id,
    name: activity.name,
    price: activity.price,
    brand: activity.title,
});

export const createProductInListFromArticle = ({
    categoryName,
    position,
}: ProductInListPayload): ProductInListResolver<ActivityArticle> => (activity) => ({
    position,
    category: categoryReplace(categoryName),
    id: activity.id,
    name: activity.name,
    price: activity.price,
    brand: activity.title,
});

export const createProductInListFromCartItem = ({
    categoryName,
    position,
}: ProductInListPayload): ProductInListResolver<ActivityCartItem> => (activity) => ({
    position,
    category: categoryReplace(categoryName),
    name: activity.product_name,
    id: activity.product_id,
    price: activity.partner_price,
    brand: activity.partner_title,
});

// Product Detail

export const createProductDetailFromArticle = ({
    categoryName,
}: ProductDetailPayload): ((v: ActivityArticle) => ProductDetail) => (activity) => ({
    category: categoryReplace(categoryName),
    name: activity.name,
    id: activity.id,
    price: activity.price,
    brand: activity.title,
    image: `${IMAGES_ACTIVITY_ROOT}${activity.id}/${activity.slug}.jpg`,
    price_strike_out: activity.price_strike_out || undefined,
});

// Products In Cart

export const createProductsInCartFromCartItems = ({
    categoryName,
}: ProductCartPayload): ((v: ActivityCartItem[]) => ProductCartItem[]) => (items) => {
    const res = items
        .map((item) =>
            item.tickets.map((ticket) => ({
                name: item.product_name,
                id: item.product_id,
                price: ticket.price,
                brand: item.partner_title,
                category: categoryReplace(categoryName || createCategoryNameFromCartItem(item)),
                variant: ticket.name,
                quantity: ticket.booked,
            }))
        )
        .reduce((acc, cur) => [...acc, ...cur], []);

    return res;
};

// Purchase

export const createPurchase = (order: Order): Purchase => ({
    id: order.id,
    affiliation: 'TripShock',
    revenue: order.gross_total,
    comission: order.comission,
    tax: Object.values(order.cart_items).reduce((acc, cur) => {
        if (cur.surcharges) {
            return acc + cur.surcharges;
        }
        return acc;
    }, 0),
    shipping: '0.00',
    coupon: order.accesskey,
});
